import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Habit, ViewState, CheckInLog, Member } from './types';
import { DEFAULT_HABITS } from './constants';
import { HabitCard } from './components/HabitCard';
import { StatsView } from './components/StatsView';
import { DiaryView } from './components/DiaryView';
import { HabitForm } from './components/HabitForm';
import { CheckInModal } from './components/CheckInModal';
import { MemberSwitcher } from './components/MemberSwitcher';
import { FloatingSettingsButton } from './components/FloatingSettingsButton';
import { AuthModal } from './components/AuthModal';
import { RedemptionView } from './components/RedemptionView';
import { ManualPointsModal } from './components/ManualPointsModal';
import { PointsHistoryView } from './components/PointsHistoryView';
import { HealthView } from './components/HealthView';
import { ArchivedHabitsView } from './components/ArchivedHabitsView';
import { ArchivedHabitDetailView } from './components/ArchivedHabitDetailView';
import {
  saveHabits, loadHabits,
  getMembersLocal, saveMembersLocal,
  getDarkMode, saveDarkMode,
  exportDataToJson, importDataFromJson, saveHabitsLocal,
  saveRedemptionItemsLocal, getRedemptionItemsLocal,
  saveRedemptionRecordsLocal, getRedemptionRecordsLocal
} from './services/storageService';
import {
  initSupabase, getCurrentUser, signOut, onAuthStateChange,
  backupToCloud, restoreFromCloud, getBackups, deleteBackup, deleteCheckInImage
} from './services/supabaseClient';
import { runDailySettlement } from './services/pointService';
import { updateMemberPoints } from './services/pointsHistoryService';
import { syncService } from './services/syncService';
import { generateUUID } from './utils/uuid';
import { healthStorageService } from './services/healthStorageService';
import {
  Plus, LayoutGrid, BarChart2, MessageCircle, Settings, ShoppingBag,
  Cloud, Moon, Sun, Palette, LogIn, LogOut, Upload, Download, FileJson, Trash2, BookOpen, History, Award, Heart, Archive
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { User as SupabaseUser } from '@supabase/supabase-js';

// Initialize Supabase with robust env loading
// Support multiple environment variable sources: Vite, Cloudflare Pages, custom bridge
let SUPABASE_URL: string = '';
let SUPABASE_KEY: string = '';

// 1. Try Vite build-time env vars (import.meta.env)
try {
  // @ts-ignore - import.meta.env is Vite-specific
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // @ts-ignore
    SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL || '';
    // @ts-ignore
    SUPABASE_KEY = import.meta.env.VITE_SUPABASE_KEY || import.meta.env.SUPABASE_KEY || '';
  }
} catch (e) {
  // ignore
}

// 2. Try process.env (for Node.js environments)
if (!SUPABASE_URL || !SUPABASE_KEY) {
  try {
    // @ts-ignore
    SUPABASE_URL = (typeof process !== 'undefined' && process.env?.SUPABASE_URL) || '';
    // @ts-ignore
    SUPABASE_KEY = (typeof process !== 'undefined' && process.env?.SUPABASE_KEY) || '';
  } catch (e) {
    // ignore
  }
}

// 3. Try global env bridge (custom injection)
if (!SUPABASE_URL || !SUPABASE_KEY) {
  const globalEnv = (typeof window !== 'undefined' ? (window as any).__ENV : undefined) || {};
  if (!SUPABASE_URL) SUPABASE_URL = globalEnv.SUPABASE_URL || '';
  if (!SUPABASE_KEY) SUPABASE_KEY = globalEnv.SUPABASE_KEY || '';
}

// 4. Try Cloudflare Pages env vars (injected at build time)
if (!SUPABASE_URL || !SUPABASE_KEY) {
  try {
    // @ts-ignore
    if (typeof __ENV__ !== 'undefined') {
      // @ts-ignore
      SUPABASE_URL = __ENV__.SUPABASE_URL || '';
      // @ts-ignore
      SUPABASE_KEY = __ENV__.SUPABASE_KEY || '';
    }
  } catch (e) {
    // ignore
  }
}

// Log initialization status
if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.error('Supabase not initialized: missing URL or KEY');
  console.error('Please set environment variables:');
  console.error('- VITE_SUPABASE_URL and VITE_SUPABASE_KEY (Vite)');
  console.error('- SUPABASE_URL and SUPABASE_KEY (Cloudflare Pages)');
  console.error('- Or inject them via window.__ENV');
} else {
  console.log('Supabase initialized successfully');
}

initSupabase(SUPABASE_URL, SUPABASE_KEY);

const App: React.FC = () => {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [currentMemberId, setCurrentMemberId] = useState<string>('');

  const [view, setView] = useState<ViewState>(ViewState.DASHBOARD);

  // Dark Mode State
  const [isDarkMode, setIsDarkMode] = useState<boolean>(getDarkMode());

  // Auth State
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backups, setBackups] = useState<any[]>([]);

  // Sync State
  const [syncStatus, setSyncStatus] = useState({ isSyncing: false, lastSyncTime: null as number | null, error: null as string | null, pendingChanges: false });

  // Modal States
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState<Habit | undefined>(undefined);

  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInHabit, setCheckInHabit] = useState<Habit | undefined>(undefined);
  const [checkInDate, setCheckInDate] = useState<string>('');

  const [isManualPointsModalOpen, setIsManualPointsModalOpen] = useState(false);

  const [initialized, setInitialized] = useState(false);
  const [showWeeklyRing, setShowWeeklyRing] = useState(true);

  // Archived habit detail state
  const [viewingArchivedHabit, setViewingArchivedHabit] = useState<Habit | null>(null);

  // File Input Ref for Import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Time ticker for notifications/reminders (every minute)
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Daily Settlement Timer - Check every minute for new day
  useEffect(() => {
    if (!initialized) return;

    const checkDailySettlement = async () => {
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');
      const yesterday = subDays(today, 1);
      const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

      // Check if we've already settled for yesterday
      for (const member of members) {
        if (member.lastSettlementDate === yesterdayStr) {
          continue; // Already settled for this member
        }

        const memberHabits = habits.filter(h => h.memberId === member.id);
        if (memberHabits.length === 0) continue;

        const defaultSettlement = member.lastSettlementDate || format(subDays(yesterday, 1), 'yyyy-MM-dd');
        const { updatedMember, updatedHabits, summary, pointsHistory } = runDailySettlement(member, memberHabits, defaultSettlement);

        // Update habits
        setHabits(prevHabits => prevHabits.map(h => {
          const found = updatedHabits.find(uh => uh.id === h.id);
          return found || h;
        }));

        // Update member with points history
        setMembers(prevMembers => prevMembers.map(m => 
          m.id === member.id ? updatedMember : m
        ));

        // Show notification if there were deductions
        if (summary.length > 0) {
          setTimeout(() => {
            alert(`${member.name}: ${summary.join(', ')}`);
          }, 500);
        }
      }
    };

    // Check immediately on load
    checkDailySettlement();

    // Then check every minute (will trigger once per day)
    const intervalId = setInterval(checkDailySettlement, 60000);

    return () => clearInterval(intervalId);
  }, [initialized, members, habits]);

  // Theme & Dark Mode Effects
  useEffect(() => {
    // Apply Dark Mode
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    saveDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Load data on mount
  useEffect(() => {
    const fetchData = async () => {
      // 1. Load Members
      const loadedMembers = await getMembersLocal();
      setMembers(loadedMembers);

      // Select first member if none selected or current ID invalid
      const initialMemberId = loadedMembers.length > 0 ? loadedMembers[0].id : '';
      setCurrentMemberId(initialMemberId);

      // 2. Load Habits
      const data = await loadHabits();
      let processedHabits: Habit[] = [];

      if (data.length > 0) {
        processedHabits = data;
      } else if (initialMemberId) {
        // Fallback for new users (assign defaults to first member)
        const defaultsWithMember = DEFAULT_HABITS.map(h => ({ ...h, memberId: initialMemberId }));
        processedHabits = defaultsWithMember;
        await saveHabits(defaultsWithMember);
      }

      setHabits(processedHabits);

      // 3. Check Auth Status
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      setInitialized(true);
    };
    fetchData();
  }, []);

  // Sync Auth State
  useEffect(() => {
    if (initialized) {
      const unsubscribe = onAuthStateChange((updatedUser) => {
        setUser(updatedUser);
      });
      return () => unsubscribe();
    }
  }, [initialized]);

  // Subscribe to sync status changes
  useEffect(() => {
    const unsubscribe = syncService.subscribeToStatus((status) => {
      setSyncStatus(status);
    });
    return () => unsubscribe();
  }, []);

  // Network recovery auto-sync
  useEffect(() => {
    const handleOnline = async () => {
      if (user && initialized) {
        console.log('[Network] Connection restored, triggering sync...');
        await syncService.sync(user.id);

        // Reload data after successful sync
        const updatedHabits = await loadHabits();
        const updatedMembers = await getMembersLocal();
        setHabits(updatedHabits);
        setMembers(updatedMembers);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user, initialized]);

  // Auto-sync when user logs in
  useEffect(() => {
    const handleAutoSync = async () => {
      if (user && initialized) {
        // 记录同步前的数据版本
        const beforeSyncHabits = habits;
        const beforeSyncMembers = members;

        const { success } = await syncService.sync(user.id);

        // 只在同步真正下载了云端数据时才更新状态
        // 如果是上传本地数据，不需要重新加载
        if (success) {
          // 检查同步是否下载了新数据（通过对比时间戳或数据）
          const afterSyncHabits = await loadHabits();
          const afterSyncMembers = await getMembersLocal();

          // 只有当数据真的变化了才更新（避免覆盖刚修改的数据）
          // 这里简单对比长度和最后修改时间
          const habitsChanged = JSON.stringify(afterSyncHabits) !== JSON.stringify(beforeSyncHabits);
          const membersChanged = JSON.stringify(afterSyncMembers) !== JSON.stringify(beforeSyncMembers);

          if (habitsChanged) {
            setHabits(afterSyncHabits);
          }
          if (membersChanged) {
            setMembers(afterSyncMembers);
          }
        }
      }
    };
    handleAutoSync();
  }, [user, initialized]); // 移除 habits 和 members 依赖，避免循环

  // Load backups when user changes
  useEffect(() => {
    const loadBackups = async () => {
      if (user) {
        const { data, error } = await getBackups(user.id);
        if (!error && data) {
          setBackups(data);
        }
      } else {
        setBackups([]);
      }
    };
    loadBackups();
  }, [user]);

  // Hide loading screen when app is initialized
  useEffect(() => {
    if (initialized) {
      const loadingScreen = document.getElementById('loading-screen');
      if (loadingScreen) {
        loadingScreen.classList.add('hidden');
      }
    }
  }, [initialized]);

  // Save on change (Auto-save local only)
  useEffect(() => {
    if (initialized) {
      saveHabits(habits);
      if (user) {
        syncService.triggerSync(user.id);
      }
    }
  }, [habits, initialized, user]);

  useEffect(() => {
    if (initialized) {
      const saveMembers = async () => {
        await saveMembersLocal(members);
        if (user) {
          syncService.triggerSync(user.id);
        }
      };
      saveMembers();
    }
  }, [members, initialized, user]);

  // Filter habits for current member
  const currentMemberHabits = useMemo(() => {
    return habits.filter(h => h.memberId === currentMemberId);
  }, [habits, currentMemberId]);

  // Filter active vs archived habits
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const activeHabits = useMemo(() => {
    const habits = currentMemberHabits.filter(h => {
      const isPermanent = h.isPermanent ?? true;
      if (isPermanent) return true;
      if (h.endDate) {
        return new Date(h.endDate) >= today;
      }
      return true;
    });
    // Sort: uncompleted first, completed last
    return habits.sort((a, b) => {
      const aDone = a.completedDates.includes(todayStr);
      const bDone = b.completedDates.includes(todayStr);
      if (aDone === bDone) return 0;
      return aDone ? 1 : -1;
    });
  }, [currentMemberHabits, todayStr]);

  const archivedHabits = useMemo(() => {
    return currentMemberHabits.filter(h => {
      const isPermanent = h.isPermanent ?? true;
      if (isPermanent) return false;
      if (h.endDate) {
        return new Date(h.endDate) < today;
      }
      return false;
    });
  }, [currentMemberHabits]);

  // --- Handlers ---

  const handleAddMember = (name: string, color: string, birthDate?: string, gender?: 'male' | 'female', avatarEmoji?: string, avatarImage?: string) => {
    const newMember: Member = {
      id: generateUUID(),
      name,
      avatarColor: color,
      avatarEmoji,
      avatarImage,
      createdAt: new Date().toISOString(),
      birthDate,
      gender
    };
    const updatedMembers = [...members, newMember];
    setMembers(updatedMembers);
    setCurrentMemberId(newMember.id);
    setView(ViewState.DASHBOARD);
  };

  const handleUpdateMember = (id: string, name: string, color: string, birthDate?: string, gender?: 'male' | 'female', avatarEmoji?: string, avatarImage?: string) => {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, name, avatarColor: color, birthDate, gender, avatarEmoji, avatarImage } : m));
  };

  const handleDeleteMember = (id: string) => {
    if (members.length <= 1) {
      alert("请至少保留一名成员");
      return;
    }
    if (window.confirm("确定要删除此成员吗？该成员的所有打卡数据将被永久删除。")) {
      const newMembers = members.filter(m => m.id !== id);
      setMembers(newMembers);
      setHabits(prev => prev.filter(h => h.memberId !== id));
      if (currentMemberId === id) {
        setCurrentMemberId(newMembers[0].id);
      }
    }
  };

  const handleToggleHabit = useCallback((id: string, date: string) => {
    setHabits(prev => {
      const habit = prev.find(h => h.id === id);
      if (!habit) return prev;

      // Check if habit is archived - prevent toggling archived habits
      const isPermanent = habit.isPermanent ?? true;
      const currentTodayStr = format(new Date(), 'yyyy-MM-dd');
      if (!isPermanent && habit.endDate && new Date(habit.endDate) < new Date(currentTodayStr)) {
        return prev; // Habit is archived, do nothing
      }

      const isCompleted = habit.completedDates.includes(date);
      let newDates = isCompleted
        ? habit.completedDates.filter(d => d !== date)
        : [...habit.completedDates, date];

      // Streak Calculation - Always recalculate based on actual check-in records
      let streak = 0;
      const todayStr = format(new Date(), 'yyyy-MM-dd');

      if (!isCompleted && date === todayStr) {
        // Checking IN today - recalculate streak from scratch
        const sortedDates = [...newDates].sort().reverse();
        
        if (habit.frequency === 'daily' || habit.frequency === 'workdays') {
          // Count consecutive days backwards from today
          streak = 0;
          for (let i = 0; i < sortedDates.length; i++) {
            const checkDateStr = format(subDays(new Date(), i), 'yyyy-MM-dd');
            if (sortedDates.includes(checkDateStr)) {
              streak++;
            } else {
              break;
            }
          }
        } else if (habit.frequency === 'weekly') {
          // Count consecutive completed weeks
          streak = 0;
          let currentWeekStart = new Date();
          currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
          
          while (true) {
            const weekDates: string[] = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(currentWeekStart);
              d.setDate(d.getDate() + i);
              const dayOfWeek = d.getDay();
              if (habit.targetDays.includes(dayOfWeek)) {
                weekDates.push(format(d, 'yyyy-MM-dd'));
              }
            }
            
            if (weekDates.length > 0 && weekDates.every(d => sortedDates.includes(d))) {
              streak++;
              currentWeekStart.setDate(currentWeekStart.getDate() - 7);
            } else {
              break;
            }
          }
        } else if (habit.frequency === 'interval') {
          // Count consecutive completed intervals
          streak = 0;
          const interval = habit.intervalDays || 2;
          let currentDate = new Date();
          
          while (true) {
            const checkDateStr = format(currentDate, 'yyyy-MM-dd');
            if (sortedDates.includes(checkDateStr)) {
              streak++;
              currentDate.setDate(currentDate.getDate() - interval);
            } else {
              break;
            }
          }
        }
      } else if (isCompleted && date === todayStr) {
        // Checking OUT today (undo) - recalculate streak
        const sortedDates = [...newDates].sort().reverse();
        
        if (habit.frequency === 'daily' || habit.frequency === 'workdays') {
          streak = 0;
          for (let i = 0; i < sortedDates.length; i++) {
            const checkDateStr = format(subDays(new Date(), i), 'yyyy-MM-dd');
            if (sortedDates.includes(checkDateStr)) {
              streak++;
            } else {
              break;
            }
          }
        } else if (habit.frequency === 'weekly') {
          streak = 0;
          let currentWeekStart = new Date();
          currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
          
          while (true) {
            const weekDates: string[] = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(currentWeekStart);
              d.setDate(d.getDate() + i);
              const dayOfWeek = d.getDay();
              if (habit.targetDays.includes(dayOfWeek)) {
                weekDates.push(format(d, 'yyyy-MM-dd'));
              }
            }
            
            if (weekDates.length > 0 && weekDates.every(d => sortedDates.includes(d))) {
              streak++;
              currentWeekStart.setDate(currentWeekStart.getDate() - 7);
            } else {
              break;
            }
          }
        } else if (habit.frequency === 'interval') {
          streak = 0;
          const interval = habit.intervalDays || 2;
          let currentDate = new Date();
          
          while (true) {
            const checkDateStr = format(currentDate, 'yyyy-MM-dd');
            if (sortedDates.includes(checkDateStr)) {
              streak++;
              currentDate.setDate(currentDate.getDate() - interval);
            } else {
              break;
            }
          }
        }
      }

      // Point Calculation
      let pointChange = 0;
      const basePoints = habit.basePoints || 0;
      const bonusThreshold = habit.streakBonusThreshold || 0;
      const pointsLog = { ...(habit.pointsLog || {}) };

      if (!isCompleted) {
        // Checking IN
        let earned = basePoints;
        // Check for bonus: double points if streak reaches or exceeds threshold
        if (bonusThreshold > 0 && streak >= bonusThreshold) {
          earned += basePoints;
        }
        pointChange = earned;
        pointsLog[date] = earned;
      } else {
        // Checking OUT (Undo)
        const previouslyEarned = pointsLog[date] || 0;
        pointChange = -previouslyEarned;
        delete pointsLog[date];
      }

      // Update Member Points with History (Side Effect in separate state update)
      if (pointChange !== 0) {
        setMembers(currMembers => currMembers.map(m => {
          if (m.id === habit.memberId) {
            return updateMemberPoints(m, pointChange, 'checkin', {
              habitId: habit.id,
              habitName: habit.title,
              date: date,
              reason: isCompleted ? `取消打卡: ${habit.title}` : `完成打卡: ${habit.title}`
            });
          }
          return m;
        }));
      }

      return prev.map(h => h.id === id ? { ...h, completedDates: newDates, streak, pointsLog } : h);
    });
  }, []);

  const handleSaveHabit = (habitData: Partial<Habit>) => {
    if (editingHabit) {
      setHabits(prev => prev.map(h => h.id === editingHabit.id ? { ...h, ...habitData } as Habit : h));
    } else {
      const newHabit: Habit = {
        ...habitData as Habit,
        id: generateUUID(),
        memberId: currentMemberId,
        createdAt: new Date().toISOString(),
        streak: 0,
        completedDates: [],
        logs: []
      };
      setHabits(prev => [...prev, newHabit]);
    }
  };

  const handleSaveLog = (habitId: string, log: CheckInLog) => {
    setHabits(prev => prev.map(h => {
      if (h.id !== habitId) return h;
      const existingLogs = h.logs || [];
      const logIndex = existingLogs.findIndex(l => l.date === log.date);
      let newLogs;
      if (logIndex >= 0) {
        newLogs = [...existingLogs];
        newLogs[logIndex] = log;
      } else {
        newLogs = [...existingLogs, log];
      }
      return { ...h, logs: newLogs };
    }));
  };

  const handleDeleteLog = async (habitId: string, log: CheckInLog) => {
    if (!confirm("确定要删除这条日记吗？对应的图片也会被删除。")) return;

    // 1. If there's an image in storage, delete it
    if (log.image && !log.image.startsWith('data:') && !log.image.startsWith('http')) {
      await deleteCheckInImage(log.image);
    }

    // 2. Update local state
    setHabits(prev => prev.map(h => {
      if (h.id !== habitId) return h;
      const newLogs = (h.logs || []).filter(l => l.id !== log.id);
      return { ...h, logs: newLogs };
    }));
  };

  const handleDeleteHabit = (id: string) => {
    setHabits(prev => prev.filter(h => h.id !== id));
  };

  // --- Auth Handlers ---
  const handleLoginSuccess = async () => {
    const u = await getCurrentUser();
    setUser(u);
  };

  const handleLogout = async () => {
    await signOut();
    setUser(null);
  };

  // --- Backup Handlers ---
  const handleCloudBackup = async () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }
    setBackupLoading(true);
    try {
      // Fetch latest redemption data to ensure backup is complete
      const rItems = await getRedemptionItemsLocal();
      const rRecords = await getRedemptionRecordsLocal();
      const healthProfiles = await healthStorageService.getProfiles();
      const healthRecords = await healthStorageService.getRecords();
      const { error } = await backupToCloud(habits, members, rItems, rRecords, healthProfiles, healthRecords, user.id);
      if (error) throw error;
      alert("云端备份成功！");
      const { data } = await getBackups(user.id);
      if (data) setBackups(data);
    } catch (e: any) {
      alert("备份失败: " + e.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCloudRestore = async (backupName: string) => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }
    if (!confirm("确定要从云端恢复数据吗？这将覆盖当前的本地数据。")) return;

    setBackupLoading(true);
    try {
      const { data, error } = await restoreFromCloud(backupName);
      if (error) throw error;
      if (data) {
        const restoredHabits = data.habits as Habit[];
        const restoredMembers = data.members as Member[];
        const restoredItems = (data.redemptionItems || []) as any[];
        const restoredRecords = (data.redemptionRecords || []) as any[];
        const restoredHealthProfiles = (data.healthProfiles || []) as any[];
        const restoredHealthRecords = (data.healthRecords || []) as any[];

        setHabits(restoredHabits);
        setMembers(restoredMembers);

        await saveHabitsLocal(restoredHabits);
        await saveMembersLocal(restoredMembers);
        await saveRedemptionItemsLocal(restoredItems);
        await saveRedemptionRecordsLocal(restoredRecords);

        // Restore health data
        for (const profile of restoredHealthProfiles) {
          await healthStorageService.saveProfile(profile);
        }
        for (const record of restoredHealthRecords) {
          await healthStorageService.saveRecord(record);
        }

        alert("数据恢复成功！");
      } else {
        alert("云端没有找到备份数据。");
      }
    } catch (e: any) {
      alert("恢复失败: " + e.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDeleteBackup = async (backupName: string) => {
    if (!confirm("确定要删除此备份吗？")) return;

    setBackupLoading(true);
    try {
      const { error } = await deleteBackup(backupName);
      if (error) throw error;
      alert("备份删除成功！");
      const { data } = await getBackups(user!.id);
      if (data) setBackups(data);
    } catch (e: any) {
      alert("删除失败: " + e.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleFileExport = async () => {
    const healthProfiles = await healthStorageService.getProfiles();
    const healthRecords = await healthStorageService.getRecords();
    exportDataToJson(habits, members, [], [], healthProfiles, healthRecords);
  };

  const handleFileImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!confirm("确定要导入此备份文件吗？这将覆盖当前数据。")) {
      e.target.value = ''; // reset
      return;
    }

    try {
      const { habits: newHabits, members: newMembers, redemptionItems, redemptionRecords, healthProfiles, healthRecords } = await importDataFromJson(file);
      setHabits(newHabits);
      setMembers(newMembers);
      // Ensure local storage is updated immediately
      await saveHabitsLocal(newHabits);
      await saveMembersLocal(newMembers);

      // Restore health data
      if (healthProfiles) {
        for (const profile of healthProfiles) {
          await healthStorageService.saveProfile(profile);
        }
      }
      if (healthRecords) {
        for (const record of healthRecords) {
          await healthStorageService.saveRecord(record);
        }
      }

      alert("文件导入成功！");
    } catch (err) {
      alert("导入失败，文件格式可能不正确。");
    } finally {
      e.target.value = ''; // reset input
    }
  };

  // --- Manual Points Adjustment Handler ---
  const handleManualPointsAdjustment = (amount: number, reason: string, type: 'reward' | 'penalty') => {
    const currentMember = members.find(m => m.id === currentMemberId);
    if (!currentMember) return;

    const pointChange = type === 'reward' ? amount : -amount;

    setMembers(prev => prev.map(m => {
      if (m.id === currentMemberId) {
        return updateMemberPoints(m, pointChange, 'manual', {
          reason: reason,
          date: format(new Date(), 'yyyy-MM-dd')
        });
      }
      return m;
    }));
  };

  const openNewHabitForm = () => {
    setEditingHabit(undefined);
    setIsFormOpen(true);
  };

  const openEditHabitForm = (habit: Habit) => {
    setEditingHabit(habit);
    setIsFormOpen(true);
  };

  const handleArchiveHabit = (id: string) => {
    if (!confirm("确定要归档结束此习惯吗？归档后将不再显示在打卡页面，积分停止计算。")) return;
    setHabits(prev => prev.map(h => {
      if (h.id !== id) return h;
      return { ...h, isPermanent: false, endDate: format(new Date(), 'yyyy-MM-dd'), archivedAt: format(new Date(), 'yyyy-MM-dd') };
    }));
  };

  const handleRestoreHabit = (id: string) => {
    setHabits(prev => prev.map(h => {
      if (h.id !== id) return h;
      return { ...h, isPermanent: true, startDate: undefined, endDate: undefined, restoredAt: format(new Date(), 'yyyy-MM-dd') };
    }));
    setViewingArchivedHabit(null);
  };

  const openCheckInModal = (habit: Habit, date: string) => {
    setCheckInHabit(habit);
    setCheckInDate(date);
    setIsCheckInModalOpen(true);
  };

  const handleCheckInAndLog = (habit: Habit, date: string) => {
    setCheckInHabit(habit);
    setCheckInDate(date);
    setIsCheckInModalOpen(true);
  };

  const renderContent = () => {
    switch (view) {
      case ViewState.DASHBOARD:
        return (
          <div className="pb-20 animate-fade-in">
            {/* Fixed Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 -mx-6 px-6 pt-6 pb-4 mb-4">
              <div className="flex justify-between items-end">
                <div>
                  <MemberSwitcher
                    members={members}
                    currentMemberId={currentMemberId}
                    onSwitch={setCurrentMemberId}
                    onAddMember={handleAddMember}
                    onUpdateMember={handleUpdateMember}
                    onDeleteMember={handleDeleteMember}
                  />
                  <h1 className="text-2xl font-bold text-slate-800 dark:text-white mt-3">
                    今日目标
                  </h1>
                </div>
                <div className="text-right">
                  <span className="text-4xl font-bold text-primary-600 dark:text-primary-400 transition-colors">
                    {activeHabits.filter(h => h.completedDates.includes(todayStr)).length}
                    <span className="text-xl text-slate-400 font-normal">/{activeHabits.length}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Weekly Overview Ring */}
            <div className="flex justify-end -mt-2 mb-1">
              <button
                onClick={() => setShowWeeklyRing(v => !v)}
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors px-2 py-1"
              >
                {showWeeklyRing ? '收起周环' : '展开周环'}
              </button>
            </div>
            {showWeeklyRing && (() => {
              const wDayOfWeek = today.getDay();
              const wMondayOffset = wDayOfWeek === 0 ? 6 : wDayOfWeek - 1;
              const wStartOfWeek = new Date(today);
              wStartOfWeek.setDate(today.getDate() - wMondayOffset);

              const wSize = 168;
              const wStrokeWidth = 10;
              const wRadius = (wSize - wStrokeWidth) / 2 - 4;
              const wCenter = wSize / 2;
              const wGap = 5;
              const wArcAngle = (360 - wGap * 7) / 7;
              const wDayLabels = ['一', '二', '三', '四', '五', '六', '日'];

              const wDays = [0, 1, 2, 3, 4, 5, 6].map(i => {
                const d = new Date(wStartOfWeek);
                d.setDate(wStartOfWeek.getDate() + i);
                const ds = format(d, 'yyyy-MM-dd');
                const dueHabits = activeHabits.filter(h => {
                  if (h.frequency === 'daily') return true;
                  if (h.frequency === 'weekly' || h.frequency === 'workdays') return h.targetDays.includes(d.getDay());
                  if (h.frequency === 'interval') return true;
                  return true;
                });
                const done = dueHabits.filter(h => h.completedDates.includes(ds)).length;
                const total = dueHabits.length;
                const ratio = total > 0 ? done / total : 0;
                return { dateStr: ds, label: wDayLabels[i], done, total, ratio, isToday: ds === todayStr };
              });

              return (
                <div className="flex justify-center mb-6">
                  <div className="relative" style={{ width: wSize, height: wSize }}>
                    <svg width={wSize} height={wSize} viewBox={`0 0 ${wSize} ${wSize}`} className="transform">
                      {wDays.map((day, i) => {
                        const sa = i * (wArcAngle + wGap) - 90;
                        const ea = i * (wArcAngle + wGap) + wArcAngle - 90;
                        const sr = sa * Math.PI / 180;
                        const er = ea * Math.PI / 180;
                        const x1 = wCenter + wRadius * Math.cos(sr);
                        const y1 = wCenter + wRadius * Math.sin(sr);
                        const x2 = wCenter + wRadius * Math.cos(er);
                        const y2 = wCenter + wRadius * Math.sin(er);
                        const path = `M ${x1} ${y1} A ${wRadius} ${wRadius} 0 0 1 ${x2} ${y2}`;
                        let color = 'rgba(148, 163, 184, 0.2)';
                        if (day.ratio >= 1) color = '#22C55E';
                        else if (day.ratio > 0) color = '#F59E0B';
                        return (
                          <path
                            key={i}
                            d={path}
                            stroke={color}
                            strokeWidth={wStrokeWidth}
                            strokeLinecap="round"
                            fill="none"
                          />
                        );
                      })}
                    </svg>
                    {wDays.filter(d => d.isToday).map(d => (
                      <div key="center" className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-slate-800 dark:text-white">{d.done}</span>
                          <span className="text-sm text-slate-400 font-normal">/{d.total}</span>
                          <div className="text-[10px] text-slate-400 mt-0.5">今日完成</div>
                        </div>
                      </div>
                    ))}
                    {/* Day labels outside ring */}
                    {wDays.map((day, i) => {
                      const labelAngle = (i * (wArcAngle + wGap) + wArcAngle / 2 - 90) * Math.PI / 180;
                      const labelRadius = wRadius + 18;
                      const lx = wCenter + labelRadius * Math.cos(labelAngle);
                      const ly = wCenter + labelRadius * Math.sin(labelAngle);
                      return (
                        <div
                          key={i}
                          className="absolute text-[10px] font-medium"
                          style={{
                            left: lx - 8,
                            top: ly - 8,
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: day.isToday ? '#0ea5e9' : day.ratio >= 1 ? '#22C55E' : day.ratio > 0 ? '#F59E0B' : '#94a3b8',
                            fontWeight: day.isToday ? 700 : 500,
                          }}
                        >
                          {day.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Scrollable Cards */}
            <div className="space-y-4">
              {activeHabits.map(habit => (
                <HabitCard
                  key={habit.id}
                  habit={habit}
                  currentTime={currentTime}
                  onToggle={handleToggleHabit}
                  onEdit={openEditHabitForm}
                  onLog={openCheckInModal}
                  onCheckInAndLog={handleCheckInAndLog}
                  onArchive={handleArchiveHabit}
                />
              ))}

              {activeHabits.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <p>还没有习惯？点击下方 + 号开始吧</p>
                </div>
              )}
            </div>
          </div>
        );
      case ViewState.STATS:
        return (
          <div className="pb-20">
            <div className="mb-4">
              <MemberSwitcher
                members={members}
                currentMemberId={currentMemberId}
                onSwitch={setCurrentMemberId}
                onAddMember={handleAddMember}
                onUpdateMember={handleUpdateMember}
                onDeleteMember={handleDeleteMember}
              />
            </div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">统计分析</h2>
              {archivedHabits.length > 0 && (
                <button
                  onClick={() => setView(ViewState.ARCHIVED_HABITS)}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <Archive size={16} />
                  已归档 ({archivedHabits.length})
                </button>
              )}
            </div>
            <StatsView habits={currentMemberHabits} />
          </div>
        );
      case ViewState.DIARY:
        return (
          <div className="pb-20">
            <DiaryView
              habits={currentMemberHabits}
              onDeleteLog={handleDeleteLog}
              onEditLog={(habit, log) => openCheckInModal(habit, log.date)}
              memberSwitcher={
                <MemberSwitcher
                  members={members}
                  currentMemberId={currentMemberId}
                  onSwitch={setCurrentMemberId}
                  onAddMember={handleAddMember}
                  onUpdateMember={handleUpdateMember}
                  onDeleteMember={handleDeleteMember}
                />
              }
            />
          </div>
        );
      case ViewState.SHOP:
        return (
          <div className="pb-20">
            <RedemptionView
              currentMember={members.find(m => m.id === currentMemberId) || members[0]}
              onMemberUpdate={(updated) => {
                setMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
                // Note: saveMembersLocal effect will trigger automatically
              }}
              memberSwitcher={
                <MemberSwitcher
                  members={members}
                  currentMemberId={currentMemberId}
                  onSwitch={setCurrentMemberId}
                  onAddMember={handleAddMember}
                  onUpdateMember={handleUpdateMember}
                  onDeleteMember={handleDeleteMember}
                />
              }
            />
          </div>
        );
      case ViewState.POINTS_HISTORY:
        return (
          <div className="pb-20">
            <div className="mb-4">
              <MemberSwitcher
                members={members}
                currentMemberId={currentMemberId}
                onSwitch={setCurrentMemberId}
                onAddMember={handleAddMember}
                onUpdateMember={handleUpdateMember}
                onDeleteMember={handleDeleteMember}
              />
            </div>
            <PointsHistoryView
              member={members.find(m => m.id === currentMemberId) || members[0]}
            />
          </div>
        );
      case ViewState.HEALTH:
        return <HealthView members={members} currentMemberId={currentMemberId} onBackToHome={() => setView(ViewState.DASHBOARD)} />;
      case ViewState.ARCHIVED_HABITS:
        return viewingArchivedHabit ? (
          <div className="pb-20">
            <ArchivedHabitDetailView
              habit={viewingArchivedHabit}
              onBack={() => setViewingArchivedHabit(null)}
              onRestore={(id) => { handleRestoreHabit(id); setViewingArchivedHabit(null); }}
              onEdit={(habit) => { setViewingArchivedHabit(null); openEditHabitForm(habit); }}
            />
          </div>
        ) : (
          <div className="pb-20">
            <div className="mb-4">
              <MemberSwitcher
                members={members}
                currentMemberId={currentMemberId}
                onSwitch={setCurrentMemberId}
                onAddMember={handleAddMember}
                onUpdateMember={handleUpdateMember}
                onDeleteMember={handleDeleteMember}
              />
            </div>
            <ArchivedHabitsView
              habits={archivedHabits}
              onRestore={handleRestoreHabit}
              onEdit={openEditHabitForm}
              onBack={() => setView(ViewState.STATS)}
              onViewDetail={(habit) => setViewingArchivedHabit(habit)}
            />
          </div>
        );
      case ViewState.SETTINGS:
        return (
          <div className="pb-20 space-y-4">
            {/* Account Settings */}
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
                <Settings size={20} /> 账户管理
              </h2>

              {user ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 rounded-full flex items-center justify-center font-bold text-lg">
                      {user.email?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{user.email}</p>
                      <p className="text-xs text-slate-400">已登录</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 py-2 text-red-500 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-colors text-sm font-medium"
                  >
                    <LogOut size={16} /> 退出登录
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-4">登录账户以开启云端备份功能，保护您的数据不丢失。</p>
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="w-full py-3 bg-primary-600 text-white rounded-xl font-bold shadow-lg shadow-primary-200 dark:shadow-none hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogIn size={18} /> 登录 / 注册
                  </button>
                </div>
              )}
            </div>

            {/* Manual Points Adjustment */}
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
                <Award size={20} /> 手动调整积分
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                为当前成员手动奖励或扣除积分，适用于额外任务、特殊奖励或惩罚等场景。
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setView(ViewState.POINTS_HISTORY)}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
                >
                  <History size={18} /> 查看历史
                </button>
                <button
                  onClick={() => setIsManualPointsModalOpen(true)}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-xl font-bold shadow-lg shadow-amber-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <Award size={18} /> 调整积分
                </button>
              </div>
            </div>

            {/* Cloud Backup */}
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
                <Cloud size={20} /> 云端同步
              </h2>

              {/* Sync Status */}
              {user && (
                <div className={`mb-4 p-3 rounded-xl flex items-center gap-3 ${syncStatus.error ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50' :
                  syncStatus.isSyncing ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50' :
                    syncStatus.pendingChanges ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/50' :
                      'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/50'
                  }`}>
                  {syncStatus.isSyncing ? (
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : syncStatus.error ? (
                    <div className="w-5 h-5 text-red-500">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    </div>
                  ) : syncStatus.pendingChanges ? (
                    <div className="w-5 h-5 text-amber-500">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4" /><path d="m16.2 7.8 2.9-2.9" /><path d="M18 12h4" /><path d="m16.2 16.2 2.9 2.9" /><path d="M12 18v4" /><path d="m4.9 19.1 2.9-2.9" /><path d="M2 12h4" /><path d="m4.9 4.9 2.9 2.9" /></svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 text-green-500">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {syncStatus.isSyncing ? '正在同步...' :
                        syncStatus.error ? '同步失败' :
                          syncStatus.pendingChanges ? '等待同步' :
                            '已同步'}
                    </p>
                    {syncStatus.lastSyncTime && !syncStatus.isSyncing && (
                      <p className="text-xs text-slate-400">
                        {new Date(syncStatus.lastSyncTime).toLocaleString('zh-CN', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                  {syncStatus.error && (
                    <button
                      onClick={async () => {
                        if (user) {
                          const { success } = await syncService.forceSync(user.id);
                          if (success) {
                            const data = await loadHabits();
                            setHabits(data);
                            const members = await getMembersLocal();
                            setMembers(members);
                          }
                        }
                      }}
                      className="px-3 py-1 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                    >
                      重试
                    </button>
                  )}
                </div>
              )}

              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                数据会自动实时同步到云端，无需手动备份。换设备登录后自动下载最新数据。
              </p>

              {process.env.SUPABASE_URL ? (
                <div className="space-y-4">
                  <button
                    onClick={handleCloudBackup}
                    disabled={backupLoading || !user}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-primary-600 text-white rounded-xl font-bold shadow-lg shadow-primary-200 dark:shadow-none hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    <Upload size={18} /> 创建新备份
                  </button>

                  {backups.length > 0 && (
                    <div className="space-y-2 mt-4">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">备份列表</p>
                      {backups.map((backup) => (
                        <div
                          key={backup.name}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-700"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                              {new Date(backup.updated_at).toLocaleString('zh-CN', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className="text-xs text-slate-400">
                              {backup.data?.habits?.length || 0} 个习惯, {backup.data?.members?.length || 0} 个成员
                            </p>
                          </div>
                          <div className="flex gap-2 ml-2">
                            <button
                              onClick={() => handleCloudRestore(backup.name)}
                              disabled={backupLoading}
                              className="p-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50"
                              title="恢复此备份"
                            >
                              <Download size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteBackup(backup.name)}
                              disabled={backupLoading}
                              className="p-2 text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                              title="删除此备份"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {backups.length === 0 && user && (
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                      <p className="text-sm text-slate-400">还没有云端备份</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-900/50">
                  <p className="text-sm text-orange-600 dark:text-orange-400">需要配置 Supabase 环境变量才能使用云端功能。</p>
                </div>
              )}
            </div>

            {/* Local Backup */}
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
                <FileJson size={20} /> 本地备份
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={handleFileExport}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Download size={16} /> 导出文件
                </button>
                <button
                  onClick={handleFileImportClick}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Upload size={16} /> 导入文件
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".json"
                  onChange={handleFileImport}
                />
              </div>
              <p className="text-xs text-slate-400 mt-3 text-center">
                导出 .json 文件到手机或电脑，确保数据永不丢失。
              </p>
            </div>

            {/* Appearance Settings */}
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
                <Palette size={20} /> 外观设置
              </h2>

              <div className="flex items-center justify-between">
                <span className="text-slate-700 dark:text-slate-300 font-medium">深色模式</span>
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${isDarkMode ? 'bg-primary-500' : 'bg-slate-200'}`}
                >
                  <div className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-sm transition-transform flex items-center justify-center ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}`}>
                    {isDarkMode ? <Moon size={14} className="text-primary-500" /> : <Sun size={14} className="text-amber-500" />}
                  </div>
                </button>
              </div>
            </div>
          </div>
        )
      default:
        return null;
    }
  };

  return (
      <div className="h-[100dvh] w-full flex justify-center bg-slate-50 dark:bg-slate-950 font-sans selection:bg-primary-200 transition-colors duration-300 overflow-hidden overscroll-none">
        {/* Status Bar Background - 沉浸式状态栏 */}
        <div className="fixed top-0 w-full max-w-md h-[env(safe-area-inset-top)] bg-white/80 dark:bg-slate-900/80 backdrop-blur-md z-40 shadow-[0_1px_2px_rgba(0,0,0,0.1)] pointer-events-none" />
        
        <div className="w-full max-w-md h-full flex flex-col relative bg-white dark:bg-slate-900 shadow-2xl transition-colors duration-300 overscroll-none">
          
          {/* Status Bar Spacer */}
          <div className="h-[env(safe-area-inset-top)] flex-shrink-0" />

          {/* Main Scrollable Area */}
          <main className={`flex-1 min-h-0 px-6 pb-20 overflow-y-auto overscroll-none no-scrollbar scroll-smooth ${view === ViewState.HEALTH ? 'hidden' : ''}`}>
          {renderContent()}
        </main>

        {/* HealthView - rendered outside main to avoid scroll conflict */}
        {view === ViewState.HEALTH && (
          <div className="absolute inset-0 z-30 bg-slate-50 dark:bg-slate-900">
            <HealthView members={members} currentMemberId={currentMemberId} onBackToHome={() => setView(ViewState.DASHBOARD)} />
          </div>
        )}

        {/* Floating Add Button (Only on Dashboard) */}
        {view === ViewState.DASHBOARD && (
          <button
            onClick={openNewHabitForm}
            className="absolute bottom-24 right-6 w-14 h-14 bg-orange-400 dark:bg-orange-400 text-white dark:text-slate-900 rounded-full shadow-xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all z-10"
          >
            <Plus size={28} strokeWidth={2.5} />
          </button>
        )}

        {/* Bottom Navigation (Hidden on HEALTH view) */}
        {view !== ViewState.HEALTH && (
          <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 px-6 py-4 flex justify-between items-center z-50 transition-colors duration-300">
            <button
              onClick={() => setView(ViewState.DASHBOARD)}
              className={`flex flex-col items-center space-y-1 ${view === ViewState.DASHBOARD ? 'text-primary-600 dark:text-white' : 'text-slate-400'}`}
            >
              <LayoutGrid size={24} />
              <span className="text-[10px] font-medium">打卡</span>
            </button>

            <button
              onClick={() => setView(ViewState.SHOP)}
              className={`flex flex-col items-center space-y-1 ${view === ViewState.SHOP ? 'text-primary-600 dark:text-white' : 'text-slate-400'}`}
            >
              <ShoppingBag size={24} />
              <span className="text-[10px] font-medium">商城</span>
            </button>

            <button
              onClick={() => setView(ViewState.DIARY)}
              className={`flex flex-col items-center space-y-1 ${view === ViewState.DIARY ? 'text-primary-600 dark:text-white' : 'text-slate-400'}`}
            >
              <MessageCircle size={24} />
              <span className="text-[10px] font-medium">日记</span>
            </button>

            <button
              onClick={() => setView(ViewState.STATS)}
              className={`flex flex-col items-center space-y-1 ${view === ViewState.STATS ? 'text-primary-600 dark:text-white' : 'text-slate-400'}`}
            >
              <BarChart2 size={24} />
              <span className="text-[10px] font-medium">统计</span>
            </button>

            <button
              onClick={() => setView(ViewState.HEALTH)}
              className={`flex flex-col items-center space-y-1 ${view === ViewState.HEALTH ? 'text-primary-600 dark:text-white' : 'text-slate-400'}`}
            >
              <Heart size={24} />
              <span className="text-[10px] font-medium">健康</span>
            </button>
          </nav>
        )}

        {/* Modals */}
        {isFormOpen && (
          <HabitForm
            initialData={editingHabit}
            onSave={handleSaveHabit}
            onClose={() => setIsFormOpen(false)}
            onDelete={editingHabit ? handleDeleteHabit : undefined}
          />
        )}

        {isCheckInModalOpen && checkInHabit && (
          <CheckInModal
            habit={checkInHabit}
            date={checkInDate}
            existingLog={checkInHabit.logs?.find(l => l.date === checkInDate)}
            onSave={handleSaveLog}
            onClose={() => setIsCheckInModalOpen(false)}
            onToggleCheckIn={handleToggleHabit}
          />
        )}

        {isAuthModalOpen && (
          <AuthModal
            onClose={() => setIsAuthModalOpen(false)}
            onLoginSuccess={handleLoginSuccess}
          />
        )}

        {isManualPointsModalOpen && (
          <ManualPointsModal
            member={members.find(m => m.id === currentMemberId) || members[0]}
            onSave={handleManualPointsAdjustment}
            onClose={() => setIsManualPointsModalOpen(false)}
          />
        )}

        {/* Floating Settings Button */}
        <FloatingSettingsButton onClick={() => setView(ViewState.SETTINGS)} />
      </div>
    </div>
  );
};

export default App;